import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { EmailAutomationFormState, invalidSenders } from './useEmailAutomation.ts';
import SelectField from '../../../core/components/Fields/SelectField.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import TagsField from '../../../core/components/Fields/TagsField.tsx';
import TextField from '../../../core/components/Fields/TextField.tsx';
import FormField from '../../../core/components/FormField.tsx';

interface EmailAutomationFormProps {
    form: EmailAutomationFormState;
    onChange: (patch: Partial<EmailAutomationFormState>) => void;
    // the agents the user may run an automation on; none for a user without agents, whose automations are personal
    agents: { id: string; name: string }[];
    projects: { id: string; name: string }[];
    channels: { id: string; name?: string | null; documentType: string }[];
    conditionTypes: { value: string; label: string }[];
    isAgentLocked?: boolean;
}

const EmailAutomationForm = ({
    form,
    onChange,
    agents,
    projects,
    channels,
    conditionTypes,
    isAgentLocked = false,
}: EmailAutomationFormProps) => {
    const { t } = useTranslation('chat');
    const invalidSender = invalidSenders(form.senders)[0];
    const personal = !form.agentId;

    return (
        <div className="flex flex-col gap-5">
            {agents.length > 0 && (
                <FormField label={t('trigger.agentLabel')} helpText={personal ? t('trigger.personalHint') : undefined}>
                    <SelectField
                        options={[
                            { value: '', label: t('trigger.runAsMe') },
                            ...agents.map((agent) => ({ value: agent.id, label: agent.name })),
                        ]}
                        value={form.agentId}
                        disabled={isAgentLocked}
                        onValueChange={(agentId) => onChange({ agentId })}
                    />
                </FormField>
            )}
            {personal && (
                <FormField label={t('trigger.projectLabel')} helpText={t('trigger.projectHint')}>
                    <SelectField
                        options={[
                            { value: '', label: t('trigger.noProject') },
                            ...projects.map((project) => ({ value: project.id, label: project.name })),
                        ]}
                        value={form.projectId}
                        onValueChange={(projectId) => onChange({ projectId })}
                    />
                </FormField>
            )}

            <FormField label={t('trigger.nameLabel')} required>
                <StringField
                    value={form.name}
                    placeholder={t('trigger.namePlaceholder')}
                    onValueChange={(name) => onChange({ name })}
                />
            </FormField>

            <FormField label={t('trigger.typeLabel')}>
                <StringField value={t('trigger.eventType.email_received')} readOnly />
            </FormField>

            <FormField label={t('trigger.channelLabel')} required>
                <SelectField
                    required
                    options={channels.map((channel) => ({
                        value: channel.id,
                        label: channel.name || channel.documentType,
                    }))}
                    placeholder={t('trigger.channelPlaceholder')}
                    value={form.channelId}
                    onValueChange={(channelId) => onChange({ channelId })}
                />
            </FormField>

            <FormField
                label={t('trigger.conditionLabel')}
                required
                helpText={t('trigger.senderHint')}
                error={invalidSender ? { message: t('trigger.invalidSender', { sender: invalidSender }) } : undefined}
            >
                <div className="flex w-full items-start gap-2">
                    {/* "Sender is" is the only condition the backend supports */}
                    <SelectField
                        className="w-32 flex-none"
                        options={conditionTypes.map((option) => ({
                            value: option.value,
                            label: t(`trigger.conditionType.${option.value}`, { defaultValue: option.label }),
                        }))}
                        value={conditionTypes[0]?.value || ''}
                        disabled={conditionTypes.length < 2}
                        aria-label={t('trigger.conditionLabel')}
                    />
                    <TagsField
                        className="flex-1"
                        value={form.senders}
                        placeholder={t('trigger.senderPlaceholder')}
                        aria-label={t('trigger.senderLabel')}
                        onValueChange={(senders) => onChange({ senders })}
                    />
                </div>
            </FormField>

            <FormField label={t('trigger.instructionLabel')} required>
                <TextField
                    rows={4}
                    value={form.instruction}
                    placeholder={t('trigger.instructionPlaceholder')}
                    onValueChange={(instruction) => onChange({ instruction })}
                />
            </FormField>
        </div>
    );
};

export default EmailAutomationForm;
